import { Moon, Sun, LogOut, Wifi, WifiOff, Bell, BellOff, RefreshCw } from 'lucide-react'
import { useThemeStore } from '../../store/themeStore'
import { useAuthStore } from '../../store/authStore'
import { useNotifications } from '../../hooks/useNotifications'
import { useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { NotificationPanel } from '../notifications/NotificationPanel'
import { SyncModal } from '../ui/SyncModal'
import { toast } from '../../utils/toast'
import { getLastSyncTime } from '../../services/syncService'
import { formatRelativeTime } from '../../utils/helpers'

export function Header() {
  const { theme, toggleTheme } = useThemeStore()
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [showNotifications, setShowNotifications] = useState(false)
  const [showSyncModal, setShowSyncModal] = useState(false)
  const [lastSyncTime, setLastSyncTime] = useState<number | null>(null)
  
  const {
    notificacoes,
    naoLidas,
    wsConnected,
    marcarComoLida,
    marcarTodasLidas,
    requestBrowserPermission,
    isBrowserNotificationEnabled,
  } = useNotifications()

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Carrega última sincronização ao montar
  useEffect(() => {
    const loadLastSync = async () => {
      const time = await getLastSyncTime()
      setLastSyncTime(time)
    }
    loadLastSync()

    // Atualiza a cada minuto
    const interval = setInterval(loadLastSync, 60000)
    return () => clearInterval(interval)
  }, [])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleRequestNotificationPermission = async () => {
    const granted = await requestBrowserPermission()
    if (granted) {
      alert('✅ Notificações habilitadas! Você receberá alertas de novos pedidos e atualizações.')
    }
  }

  return (
    <header className="sticky top-0 z-30 border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <div className="flex h-16 items-center justify-between px-6">
        <div className="flex items-center gap-4">
          {/* Online/Offline indicator */}
          <div className="flex items-center gap-2">
            {isOnline ? (
              <>
                <Wifi className="h-5 w-5 text-green-500" />
                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                  Online
                </span>
              </>
            ) : (
              <>
                <WifiOff className="h-5 w-5 text-orange-500" />
                <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
                  Offline
                </span>
              </>
            )}
          </div>

          {/* WebSocket indicator */}
          <div className="flex items-center gap-2 border-l border-gray-300 dark:border-gray-600 pl-4">
            <div className={`h-2 w-2 rounded-full ${wsConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
            <span className={`text-xs font-medium ${wsConnected ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
              {wsConnected ? 'Tempo Real' : 'Conectando...'}
            </span>
          </div>

          {/* Última sincronização */}
          <div className="flex items-center gap-2 border-l border-gray-300 dark:border-gray-600 pl-4">
            <RefreshCw className="h-3 w-3 text-gray-400 dark:text-gray-500" />
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
              {formatRelativeTime(lastSyncTime)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative rounded-lg p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              aria-label="Notificações"
            >
              <Bell className="h-5 w-5" />
              {naoLidas > 0 && (
                <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                  {naoLidas > 9 ? '9+' : naoLidas}
                </span>
              )}
            </button>

            {showNotifications && (
              <NotificationPanel
                notificacoes={notificacoes}
                naoLidas={naoLidas}
                onMarcarLida={marcarComoLida}
                onMarcarTodasLidas={marcarTodasLidas}
                onClose={() => setShowNotifications(false)}
              />
            )}
          </div>

          {/* Browser notification toggle */}
          {!isBrowserNotificationEnabled && (
            <button
              onClick={handleRequestNotificationPermission}
              className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              title="Ativar notificações do sistema"
              aria-label="Ativar notificações"
            >
              <BellOff className="h-5 w-5 text-orange-500" />
            </button>
          )}

          {/* Sync button */}
          <button
            onClick={() => setShowSyncModal(true)}
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            title="Sincronizar dados"
            aria-label="Sincronizar dados"
          >
            <RefreshCw className="h-5 w-5" />
          </button>

          {/* Update app button */}
          <button
            onClick={() => {
              if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                navigator.serviceWorker.getRegistration().then(reg => {
                  if (reg) {
                    reg.update().then(() => {
                      toast.success('Verificando atualizações...')
                    })
                  }
                })
              }
              // Sempre recarrega para garantir a última versão estática
              setTimeout(() => window.location.reload(), 800)
            }}
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            title="Atualizar versão da plataforma"
            aria-label="Atualizar versão da plataforma"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
              <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
              <path d="M16 21h5v-5" />
            </svg>
          </button>

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            aria-label="Toggle theme"
          >
            {theme === 'light' ? (
              <Moon className="h-5 w-5" />
            ) : (
              <Sun className="h-5 w-5" />
            )}
          </button>

          {/* User info */}
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {user?.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {user?.email}
              </p>
            </div>

            {/* Logout button */}
            <button
              onClick={handleLogout}
              className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              aria-label="Logout"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Modal de Sincronização */}
      <SyncModal
        isOpen={showSyncModal}
        onClose={() => setShowSyncModal(false)}
        onComplete={async () => {
          toast.success('Dados sincronizados com sucesso!')
          // Atualiza o indicador de última sync
          const time = await getLastSyncTime()
          setLastSyncTime(time)
          // Recarrega a página para atualizar todos os dados
          window.location.reload()
        }}
      />
    </header>
  )
}
